import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Divider, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  fetchInfoCapitalGoalsIt,
  deleteSuccessFactor,
  fetchItType,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function SmInfoCapitalGoalItDetailPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const goals = await fetchInfoCapitalGoalsIt(businessDomainId);
      const found = (Array.isArray(goals) ? goals : []).find(
        (g) => String(g.successFactorId) === String(successFactorId)
      );
      setData(found || null);
      if (found?.itTypeId) {
        try {
          const it = await fetchItType(found.itTypeId);
          setTypeName(it?.itTypeName || "");
        } catch {}
      }
    } catch (e) {
      notify.apiError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteSuccessFactor(successFactorId);
      notify.success(t("message.delete.success"));
      navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-it`, { replace: true });
    } catch (e) {
      notify.apiError(e);
    }
  };

  if (!data) return null;

  const indicators = data.indicators || [];
  const sources = data.sources || [];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("infoCapitalGoalIt.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-it/${successFactorId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("successFactor.name")} value={data.successFactorName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("successFactor.description")} value={data.successFactorDescription} />
          </Grid>
          {data.itTypeId && (
            <Grid size={12}>
              <DetailField label={t("itType.label")} value={typeName || data.itTypeId} />
            </Grid>
          )}
        </Grid>
      </DetailSection>

      {indicators.length > 0 && (
        <DetailSection sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("businessKpi.indicatorName")}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {indicators.map((ind, i) => (
            <Typography key={i} variant="body2">
              ・{ind.indicatorName}（{ind.unit}）
            </Typography>
          ))}
        </DetailSection>
      )}

      {sources.length > 0 && (
        <DetailSection sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("sm.relatedGoals")}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {sources.map((s, i) => (
            <Typography key={i} variant="body2">
              ・{s.successFactorName}
            </Typography>
          ))}
        </DetailSection>
      )}

      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-it`)}
        >
          {t("common.back")}
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
